import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Returns true once `ref` intersects the viewport (with optional rootMargin).
 * Used to defer heavy embeds (e.g. Google Maps iframes) until the user scrolls
 * near them, keeping third-party JS off the critical path on initial load.
 */
export function useLazyInView<T extends HTMLElement = HTMLDivElement>(
  rootMargin = '200px 0px',
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return [ref, visible];
}
